/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Success Messages Hook
 *
 * Standardized hook for handling success messages from URL parameters after CRUD operations.
 * Eliminates duplicate useEffect blocks across Clients, Projects, Suppliers, and Proposals pages.
 *
 * Pattern: URL param (created=1) → Toast message → Clean URL
 */

"use client";

import { logger } from "@/lib/utils/logger";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { useEffect } from "react";
import { toast } from "sonner";

/**
 * Success operation types that can be detected from URL parameters
 */
type SuccessOperation = "created" | "updated" | "deleted";

/**
 * Options for useSuccessMessages hook
 */
interface UseSuccessMessagesOptions {
  /**
   * Entity label for message generation (e.g., "Client", "Project", "Estimate")
   * Used to generate messages like "Client created", "Project updated", etc.
   */
  entityLabel: string;

  /**
   * Next.js App Router instance
   */
  router: AppRouterInstance;

  /**
   * URL search parameters (from useSearchParams)
   * Optional - if not provided, hook will skip processing
   */
  searchParams?: ReadonlyURLSearchParams | null;

  /**
   * @deprecated No longer needed — toast is imported from sonner directly.
   * Kept for backward compatibility; ignored if provided.
   */
  toast?: unknown;

  /**
   * Optional custom message mappings
   * If not provided, defaults to "{entityLabel} created/updated/deleted"
   */
  customMessages?: Partial<Record<SuccessOperation, string>>;
}

/**
 * Hook to handle success messages from URL parameters
 *
 * Detects URL parameters like ?created=1, ?updated=1, ?deleted=1
 * Shows toast notification and cleans URL
 *
 * @example
 * ```typescript
 * const router = useRouter();
 * const searchParams = useSearchParams();
 *
 * useSuccessMessages({
 *   entityLabel: "Client",
 *   router,
 *   searchParams,
 * });
 * ```
 *
 * When user returns from /app/clients/new with ?created=1:
 * - Shows toast: "Client created"
 * - Cleans URL to /app/clients
 */
export function useSuccessMessages({
  entityLabel,
  router,
  searchParams,
  customMessages,
}: UseSuccessMessagesOptions): void {
  useEffect(() => {
    // Guard against missing dependencies or SSR
    if (!searchParams || !router || typeof window === "undefined") {
      return;
    }

    try {
      // Default message mappings
      const defaultMappings: Record<SuccessOperation, string> = {
        created: `${entityLabel} created`,
        updated: `${entityLabel} updated`,
        deleted: `${entityLabel} deleted`,
      };

      // Merge with custom messages if provided
      const mappings: Record<SuccessOperation, string> = {
        ...defaultMappings,
        ...customMessages,
      };

      let hasChanges = false;

      // Check each operation type
      for (const operation of [
        "created",
        "updated",
        "deleted",
      ] as SuccessOperation[]) {
        if (searchParams.get(operation) === "1") {
          const message = mappings[operation];
          if (message) {
            toast.success(message);
            hasChanges = true;
          }
        }
      }

      // Clean URL if any messages were shown
      if (hasChanges) {
        const newSearchParams = new URLSearchParams(searchParams.toString());

        // Remove success operation parameters
        for (const operation of [
          "created",
          "updated",
          "deleted",
        ] as SuccessOperation[]) {
          newSearchParams.delete(operation);
        }

        // Build new URL
        const newUrl = `${window.location.pathname}${
          newSearchParams.toString() ? `?${newSearchParams.toString()}` : ""
        }`;

        // Replace URL without page reload
        router.replace(newUrl);
      }
    } catch (error) {
      // Log error but don't break the page
      // Extract error details since Error objects don't serialize well
      const errorDetails =
        error instanceof Error
          ? {
              message: error.message,
              name: error.name,
              stack: error.stack,
            }
          : { value: String(error) };

      logger.error("Failed to process success messages from URL", {
        error: errorDetails,
        searchParamsString: searchParams?.toString() ?? "null",
        hasRouter: !!router,
        hasWindow: typeof window !== "undefined",
      });
    }
  }, [searchParams, router, entityLabel, customMessages]);
}
